import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import { useDragScroll } from '../hooks/useDragScroll';
import DDayPopup from '../components/DDayPopup';
import ResultsModal from '../components/ResultsModal';
import { daysUntilNum, avatarColor, initials, formatCategories } from '../components/TournamentCard';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function surfaceToPhotoDash(surface) {
  if (surface === 'beach') return 'photo-beach';
  if (surface === 'green') return 'photo-sunset';
  if (surface === 'gymnase') return 'photo-court';
  return 'photo-beach';
}

function splitNameDash(name) {
  if (!name) return ['', ''];
  const words = name.trim().split(' ');
  if (words.length <= 2) return [name, ''];
  const mid = Math.ceil(words.length / 2);
  return [words.slice(0, mid).join(' '), words.slice(mid).join(' ')];
}

function formatTimeDash(time) {
  if (!time) return '';
  const [h, m] = String(time).split(':');
  return `${parseInt(h, 10)}h${m || '00'}`;
}

function locationPartsDash(loc) {
  if (!loc) return { venue: '', city: '' };
  const parts = loc.split(',').map(p => p.trim()).filter(p => p && !/^france$/i.test(p));
  if (parts.length === 0) return { venue: '', city: '' };
  if (parts.length === 1) return { venue: '', city: parts[0] };
  return { venue: parts[0], city: parts[parts.length - 1] };
}

// Ville seule (compacte) pour les cartes — évite les adresses complètes qui débordent.
function cityOnly(t) {
  if (t.city) return t.city;
  const parts = (t.location || '').split(',').map(s => s.trim()).filter(Boolean);
  const cp = parts.find(p => /^\d{4,5}\s/.test(p));           // « 22240 Fréhel » → « Fréhel »
  if (cp) return cp.replace(/^\d{4,5}\s+/, '').replace(/\.$/, '');
  return (parts[parts.length - 1] || '').replace(/\.$/, '');
}

function HeroCard({ entry }) {
  const navigate = useNavigate();
  const t = entry.tournament;
  const days = daysUntilNum(t.date);
  const photoClass = surfaceToPhotoDash(t.surface);
  const [nameLine1, nameLine2] = splitNameDash(t.name);
  const memberDetails = entry.myTeam?.memberDetails || [];
  const memberCount = (entry.myTeam?.members?.length || 0) + (entry.myTeam?.externalMembers?.length || 0);
  const maxMembers = entry.myTeam?.maxSize || 0;
  const isComplete = maxMembers > 0 && memberCount >= maxMembers;
  const { city } = locationPartsDash(t.location);
  const dateShort = t.date ? new Date(t.date + 'T12:00').toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' }).replace(/\./g, '') : '';
  const timeShort = formatTimeDash(t.time);
  const teamCats = entry.myTeam ? formatCategories(entry.myTeam) : [];
  const cats = teamCats.length ? teamCats : formatCategories(t); // fallback sur tournoi si équipe sans catégorie
  const dateTimeLine = [dateShort, timeShort].filter(Boolean).join(' · ');
  const externalMembers = (entry.myTeam?.externalMembers || []).map((ext, i) => {
    const parts = (ext.name || 'Joueur externe').trim().split(/\s+/);
    return { id: ext.id || `ext-${i}`, firstName: parts[0], lastName: parts.slice(1).join(' '), isExternal: true };
  });
  const allMembers = [...memberDetails, ...externalMembers];
  const visibleMembers = allMembers.slice(0, 5);

  return (
    <div className="tourney-hero sand-grain on-hero"
      style={{ margin: '0 16px 24px', borderRadius: 'var(--r-xl)', cursor: 'pointer' }}
      onClick={() => navigate(`/tournaments/${t.id}`)}>
      <div className={`hero-photo ${photoClass}`} />
      <div className="hero-tint" />
      <div style={{ padding: '20px 20px 22px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <span className="chip chip-sun">Prochain tournoi</span>
          <span className="mono" style={{ color: 'rgba(255,255,255,0.75)' }}>
            {days <= 0 ? "Auj." : days === 1 ? 'Demain' : `J–${days}`}
          </span>
        </div>

        <div className="display" style={{ fontSize: 38, lineHeight: 0.96 }}>{nameLine1}</div>
        {nameLine2 && <div className="display" style={{ fontSize: 38, lineHeight: 0.96 }}>{nameLine2}</div>}

        {city && (
          <div className="display" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: 17 }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <path d="M12 22s7-7.5 7-13a7 7 0 1 0-14 0c0 5.5 7 13 7 13z"/><circle cx="12" cy="9" r="2.5"/>
            </svg>
            <span>{city}</span>
          </div>
        )}
        {dateTimeLine && (
          <div className="display" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, fontSize: 15, color: 'rgba(255,255,255,0.9)' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>
            </svg>
            <span>{dateTimeLine}</span>
          </div>
        )}

        {entry.myTeam && (
          <div style={{ marginTop: 16, padding: '14px 16px', borderRadius: 'var(--r-md)',
            background: 'rgba(255,255,255,0.14)', backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.22)', cursor: 'pointer' }}
            onClick={e => { e.stopPropagation(); navigate(`/tournaments/${t.id}?tab=equipes`); }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="mono" style={{ color: 'rgba(255,255,255,0.6)', fontSize: 10 }}>Ton équipe</span>
                  <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0,
                    color: isComplete ? '#A5D6A7' : '#FFCC80',
                    background: isComplete ? 'rgba(165,214,167,0.18)' : 'rgba(255,204,128,0.18)',
                    padding: '2px 7px', borderRadius: 6 }}>
                    {isComplete ? 'Complète' : `${memberCount}/${maxMembers}`}
                  </span>
                </div>
                <div style={{ fontWeight: 700, fontSize: 16, marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {entry.myTeam.name}
                </div>
              </div>
              {cats.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                  <span className="mono" style={{ color: 'rgba(255,255,255,0.6)', fontSize: 10 }}>Format</span>
                  {cats.map(c => <span key={c} style={{ fontWeight: 700, fontSize: 16, marginTop: 4 }}>{c}</span>)}
                </div>
              )}
            </div>
            {visibleMembers.length > 0 && (
              <div style={{ display: 'flex' }}>
                {visibleMembers.map((m) => (
                  <div key={m.id}
                    style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, cursor: m.isExternal ? 'default' : 'pointer' }}
                    onClick={m.isExternal ? undefined : (e => { e.stopPropagation(); navigate(`/profil/${m.id}`); })}>
                    <div className={m.avatarUrl || m.isExternal ? '' : avatarColor(m.id)}
                      style={{ width: 44, height: 44, borderRadius: '50%', overflow: 'hidden',
                        border: m.isExternal ? '2px dashed rgba(255,255,255,0.5)' : '2px solid rgba(255,255,255,0.35)',
                        background: m.isExternal ? 'rgba(255,255,255,0.15)' : undefined,
                        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {m.avatarUrl
                        ? <img src={m.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <span style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>{initials(m.firstName, m.lastName)}</span>
                      }
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.9)',
                      maxWidth: 64, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {m.firstName}
                    </span>
                  </div>
                ))}
                {allMembers.length > 5 && (
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 44, height: 44, borderRadius: '50%',
                      background: 'rgba(255,255,255,0.2)', border: '2px solid rgba(255,255,255,0.35)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 700, color: '#fff' }}>
                      +{allMembers.length - 5}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {!entry.myTeam && (
          <div style={{ marginTop: 16, padding: '10px 14px', borderRadius: 'var(--r-md)',
            background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)' }}>
            <span className="mono" style={{ color: 'rgba(255,255,255,0.65)' }}>Sans équipe</span>
          </div>
        )}
      </div>
    </div>
  );
}

function UpcomingRow({ entry, onClick }) {
  const t = entry.tournament;
  const d = new Date(t.date + 'T12:00');
  const mShort = d.toLocaleDateString('fr-FR', { month: 'short' }).replace('.', '').toUpperCase();
  const dNum = d.getDate();
  const days = daysUntilNum(t.date);
  const city = t.location ? t.location.split(', ').slice(-1)[0].replace(/\.$/, '') : '';
  const formatLabel = formatCategories(t).join(' · ') || '';
  const teamName = entry.myTeam?.name;
  const members = entry.myTeam?.memberDetails || [];
  const visible = members.slice(0, 4);
  const extra = members.length - visible.length;

  return (
    <div className="card row" style={{ padding: 12, gap: 12, cursor: 'pointer' }} onClick={onClick}>
      <div style={{ width: 52, minHeight: 52, borderRadius: 12, background: 'var(--ink-3)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <span className="mono" style={{ color: 'var(--text-3)', fontSize: 9 }}>{mShort}</span>
        <span className="display" style={{ fontSize: 24, color: 'var(--text)' }}>{dNum}</span>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name}</div>
        <div className="mono" style={{ color: 'var(--text-3)', marginTop: 3 }}>
          {city}{formatLabel ? <>{city ? ' · ' : ''}<span style={{ whiteSpace: 'nowrap' }}>{formatLabel}</span></> : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
          {teamName && (
            <div style={{ flex: 1, minWidth: 0, fontSize: 12, color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {teamName}
            </div>
          )}
          {visible.length > 0 && (
            <div style={{ display: 'flex', flexShrink: 0 }}>
              {visible.map((m, i) => (
                <div key={m.id} title={m.firstName} style={{ marginLeft: i ? -8 : 0, width: 24, height: 24, borderRadius: '50%', overflow: 'hidden', border: '2px solid var(--ink)', flexShrink: 0 }}>
                  {m.avatarUrl
                    ? <img src={m.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <div className={avatarColor(m.id)} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: '#fff' }}>{initials(m.firstName, m.lastName)}</div>}
                </div>
              ))}
              {extra > 0 && (
                <div style={{ marginLeft: -8, width: 24, height: 24, borderRadius: '50%', border: '2px solid var(--ink)', background: 'var(--ink-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: 'var(--text-2)', flexShrink: 0 }}>+{extra}</div>
              )}
            </div>
          )}
        </div>
      </div>
      <span className={`chip ${days <= 0 ? 'chip-sun' : days <= 7 ? 'chip-coral' : 'chip-cyan'}`}
        style={{ flexShrink: 0, alignSelf: 'center' }}>
        {days <= 0 ? 'Auj.' : days === 1 ? 'Demain' : `J–${days}`}
      </span>
    </div>
  );
}

function FriendCard({ item }) {
  const navigate = useNavigate();
  const t = item.tournament;
  const photoClass = surfaceToPhotoDash(t.surface);
  const days = daysUntilNum(t.date);

  return (
    <div className="tourney-hero on-hero" style={{ width: 220, height: 168, borderRadius: 'var(--r-lg)',
      flexShrink: 0, padding: 14, display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
      cursor: 'pointer' }}
      onClick={() => navigate(`/tournaments/${t.id}`)}>
      <div className={`hero-photo ${photoClass}`} />
      <div className="hero-tint" />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <span className="mono" style={{ color: 'rgba(255,255,255,0.75)' }}>
          {days <= 0 ? 'Auj.' : `J–${days}`}
        </span>
      </div>
      <div>
        <div className="display" style={{ fontSize: 20, lineHeight: 1.15 }}>{t.name}</div>
        <div className="mono" style={{ color: 'rgba(255,255,255,0.65)', marginTop: 4 }}>{cityOnly(t)}</div>
        {item.friends && item.friends.length > 0 && (
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ display: 'flex' }}>
              {item.friends.slice(0, 3).map((f, i) => (
                <div key={f.id} className={`avatar-sm ${avatarColor(f.id)}`}
                  style={{ marginLeft: i > 0 ? -6 : 0, width: 22, height: 22, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9,
                    fontWeight: 700, color: '#fff', border: '1.5px solid rgba(255,255,255,0.3)', zIndex: 3 - i }}>
                  {initials(f.firstName, f.lastName)}
                </div>
              ))}
            </div>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>
              {item.friends.length} ami{item.friends.length > 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function DiscoverCard({ t }) {
  const navigate = useNavigate();
  const photoClass = surfaceToPhotoDash(t.surface);
  const days = daysUntilNum(t.date);

  return (
    <div className="tourney-hero on-hero" style={{ width: 200, height: 148, borderRadius: 'var(--r-lg)',
      flexShrink: 0, padding: 14, display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
      cursor: 'pointer' }}
      onClick={() => navigate(`/tournaments/${t.id}`)}>
      <div className={`hero-photo ${photoClass}`} />
      <div className="hero-tint" />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <span className="mono" style={{ color: 'rgba(255,255,255,0.75)' }}>
          {days <= 0 ? 'Auj.' : `J–${days}`}
        </span>
      </div>
      <div>
        <div className="display" style={{ fontSize: 18, lineHeight: 1.15 }}>{t.name}</div>
        <div className="mono" style={{ color: 'rgba(255,255,255,0.65)', marginTop: 3 }}>{cityOnly(t)}</div>
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="card" style={{ margin: '0 16px 12px', padding: 16, pointerEvents: 'none', height: 240, background: 'var(--ink-3)' }} />
  );
}

const CACHE_KEY = 'dashboard_my_v2';

function Dashboard({ user, onLogout, chatUnread = 0 }) {
  const [myEntries, setMyEntries] = useState(() => {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [friendEntries, setFriendEntries] = useState([]);
  const [discoverEntries, setDiscoverEntries] = useState([]);
  const [loading, setLoading] = useState(() => {
    try { return !sessionStorage.getItem(CACHE_KEY); } catch { return true; }
  });
  const [ddayTournament, setDdayTournament] = useState(null);
  const [resultsEntry, setResultsEntry]     = useState(null);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  useEffect(() => {
    if (!myEntries.length) return;
    const d = new Date();
    const today = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    const key = `dday_shown_${today}`;
    if (localStorage.getItem(key)) return;
    const todayEntry = myEntries.find(e => e.tournament.date === today);
    if (todayEntry) {
      setDdayTournament(todayEntry.tournament);
      localStorage.setItem(key, '1');
    }
  }, [myEntries]);

  useEffect(() => {
    if (!myEntries.length || loading) return;
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    const isResultsUnlocked = (e) => {
      if (e.tournament.date < todayStr) return true;
      if (e.tournament.date === todayStr) {
        const [h, m] = (e.tournament.time || '10:00').split(':').map(Number);
        const startMs = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m).getTime();
        return now.getTime() >= startMs + 8 * 3600 * 1000;
      }
      return false;
    };
    try {
      const skipped = JSON.parse(localStorage.getItem('skipped_results') || '[]');
      const pending = myEntries
        .filter(e => isResultsUnlocked(e) && e.myTeam && !e.myTeam.results && !skipped.includes(e.tournament.id))
        .sort((a, b) => b.tournament.date.localeCompare(a.tournament.date));
      if (pending.length > 0) setResultsEntry(pending[0]);
    } catch {}
  }, [myEntries, loading]);

  const fetchAll = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [myRes, friendRes] = await Promise.all([
        axios.get(`${API_URL}/api/users/me/tournaments`, { headers }),
        axios.get(`${API_URL}/api/users/me/group-activity`, { headers }).catch(() => ({ data: [] })),
      ]);
      const my = myRes.data || [];
      const friends = friendRes.data || [];
      setMyEntries(my);
      setFriendEntries(friends);
      try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(my)); } catch {}

      const knownIds = new Set([
        ...my.map(e => e.tournament.id),
        ...friends.map(f => f.tournament.id),
      ]);
      try {
        const discRes = await axios.get(`${API_URL}/api/tournaments/search`, {
          params: { limit: 20 },
          headers,
        });
        const all = Array.isArray(discRes.data) ? discRes.data : (discRes.data?.tournaments || []);
        const today = new Date().toISOString().split('T')[0];
        setDiscoverEntries(
          all.filter(t => !knownIds.has(t.id) && (t.date || '') >= today).slice(0, 10)
        );
      } catch {}
    } catch (err) {
      if (err.response?.status === 404) setMyEntries([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const { containerRef: pageRef, onTouchStart: ptrStart, onTouchEnd: ptrEnd, refreshing: ptrRefreshing } = usePullToRefresh(fetchAll);

  const _d = new Date();
  const todayStr = `${_d.getFullYear()}-${String(_d.getMonth()+1).padStart(2,'0')}-${String(_d.getDate()).padStart(2,'0')}`;

  const upcomingMine = myEntries
    .filter(e => e.tournament.date >= todayStr)
    .sort((a, b) => a.tournament.date.localeCompare(b.tournament.date));

  const heroEntry = upcomingMine.find(e => e.myTeam) || null;

  const carousel1 = upcomingMine.filter(e => e.myTeam && e.tournament.id !== heroEntry?.tournament.id);
  const carousel2 = friendEntries;
  const carousel3 = discoverEntries;

  const drag2 = useDragScroll();
  const drag3 = useDragScroll();

  const firstNameOrPseudo = user.firstName || user.pseudo?.split(' ')[0] || '';

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, background: 'var(--ink)' }}>

      {/* ── Header ── */}
      <div style={{ padding: '16px 20px 8px', background: 'var(--ink)', flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 8 }}>
              Salut {firstNameOrPseudo} <span aria-hidden="true" style={{ fontSize: 20 }}>🏐</span>
            </div>
          </div>
          <button aria-label="Notifications" style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--ink-3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: 'none', cursor: 'pointer', color: 'var(--text)', position: 'relative' }}
            onClick={() => navigate('/notifications')}>
            <svg width="25" height="25" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
          </button>
        </div>
      </div>

      {/* ── Scrollable content ── */}
      <div className="page-content" ref={pageRef} onTouchStart={ptrStart} onTouchEnd={ptrEnd}
        style={{ paddingBottom: 100 }}>
        {ptrRefreshing && (
          <div className="ptr-indicator"><div className="ptr-spinner" /><span>Actualisation…</span></div>
        )}

        {loading ? (
          <><SkeletonCard /><SkeletonCard /></>
        ) : (
          <>
            {/* Hero – prochain tournoi inscrit */}
            {heroEntry && <HeroCard entry={heroEntry} />}

            {!heroEntry && (
              <div style={{ margin: '8px 16px 24px' }}>
                <div className="card" style={{ padding: 24, textAlign: 'center' }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>🏐</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>Aucun tournoi à venir</div>
                  <div style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 16 }}>
                    Trouve ton prochain tournoi ou des partenaires de jeu.
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={() => navigate('/recherche')}>Trouver un tournoi</button>
                </div>
              </div>
            )}

            {/* Section: À venir · Mes tournois */}
            {carousel1.length > 0 && (
              <>
                <div className="section-title">
                  <h3>À venir · Mes tournois</h3>
                </div>
                <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
                  {carousel1.slice(0, 5).map((entry, i) => (
                    <UpcomingRow key={i} entry={entry} onClick={() => navigate(`/tournaments/${entry.tournament.id}`)} />
                  ))}
                </div>
              </>
            )}

            {/* Section: Tes amis y vont */}
            <div className="section-title">
              <h3>Tes amis y vont</h3>
            </div>
            {carousel2.length > 0 ? (
              <div ref={drag2.ref} onMouseDown={drag2.onMouseDown} onMouseMove={drag2.onMouseMove} onMouseUp={drag2.onMouseUp} onMouseLeave={drag2.onMouseLeave} style={{ overflowX: 'auto', paddingLeft: 16, paddingBottom: 4, marginBottom: 24 }}>
                <div style={{ display: 'flex', gap: 12, paddingRight: 16 }}>
                  {carousel2.map((item, i) => (
                    <FriendCard key={i} item={item} />
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ padding: '0 16px', marginBottom: 24 }}>
                <div className="card" style={{ padding: 20, textAlign: 'center' }}>
                  <div style={{ fontSize: 30, marginBottom: 8 }} aria-hidden="true">👥</div>
                  <div style={{ fontSize: 13, color: 'var(--text-3)', lineHeight: 1.55, marginBottom: 14 }}>
                    Ici tu verras les tournois auxquels participent tes amis. Rejoins un groupe ou crée-en un et invite tes amis.
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={() => navigate('/profile?tab=groups')}>Voir mes groupes</button>
                </div>
              </div>
            )}

            {/* Section: À découvrir */}
            {carousel3.length > 0 && (
              <>
                <div className="section-title">
                  <h3>À découvrir</h3>
                  <button className="mono" style={{ color: 'var(--sun-text)', background: 'none', border: 'none', cursor: 'pointer' }}
                    onClick={() => navigate('/recherche')}>Tout</button>
                </div>
                <div ref={drag3.ref} onMouseDown={drag3.onMouseDown} onMouseMove={drag3.onMouseMove} onMouseUp={drag3.onMouseUp} onMouseLeave={drag3.onMouseLeave} style={{ overflowX: 'auto', paddingLeft: 16, paddingBottom: 4, marginBottom: 24 }}>
                  <div style={{ display: 'flex', gap: 12, paddingRight: 16 }}>
                    {carousel3.slice(0, 8).map((t, i) => (
                      <DiscoverCard key={i} t={t} />
                    ))}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {ddayTournament && (
        <DDayPopup tournament={ddayTournament} onClose={() => setDdayTournament(null)} />
      )}

      {resultsEntry && !ddayTournament && (
        <ResultsModal
          tournament={resultsEntry.tournament}
          team={resultsEntry.myTeam}
          token={token}
          onClose={(saved) => {
            setResultsEntry(null);
            if (saved) fetchAll();
          }}
        />
      )}

    </div>
  );
}

export default Dashboard;
